import { liveTriviaModule } from '@/store/liveTrivia/module'

/** Exams with Live Trivia. v1 is EMT only. */
const liveTriviaExamGuids: readonly string[] = [ 'PPEF01' ]

export type TLivePlayerStatus = 'notJoined' | 'playing' | 'lockedIn' | 'eliminated' | 'spectating' | 'winner'

const getIsLiveTriviaExam = (examGuid: string | undefined) => {
    return !!examGuid && liveTriviaExamGuids.includes(examGuid)
}

/** Now, on the server's clock. Not reactive on its own — pair it with a ticking ref in components. */
const getServerNowMs = () => {
    return Date.now() + liveTriviaModule.state.serverClockOffsetMs
}

const getUpcomingGameForExam = (examGuid: string | undefined) => {
    const { upcomingGame, upcomingGameExamGuid } = liveTriviaModule.state
    if (!examGuid || upcomingGameExamGuid !== examGuid) {
        return null
    }
    return upcomingGame
}

/**
 * Where the signed-in user stands in the followed game:
 * - notJoined: lobby is open and they haven't joined yet
 * - playing: joined and still alive
 * - lockedIn: alive and has already answered the question in play
 * - eliminated: joined but knocked out; can keep watching
 * - spectating: never joined and the game has started (late arrival, or not premium)
 * - winner: drawn as the winner at the end
 */
const getPlayerStatus = (): TLivePlayerStatus | null => {
    const { game, player, pendingAnswer } = liveTriviaModule.state
    if (!game) {
        return null
    }
    if (player?.isWinner) {
        return 'winner'
    }
    if (!player?.joined) {
        return game.status === 'scheduled' ? 'notJoined' : 'spectating'
    }
    if (!player.alive) {
        return 'eliminated'
    }

    const questionIndex = game.currentIndex
    const isQuestionInPlay = game.status === 'question_open' || game.status === 'question_locked'
    const hasAnsweredQuestionInPlay = questionIndex !== null
        && (questionIndex in player.answers || pendingAnswer?.index === questionIndex)
    if (isQuestionInPlay && hasAnsweredQuestionInPlay) {
        return 'lockedIn'
    }
    return 'playing'
}

/** The choice the user locked in for the current question (accepted or still in flight), if any. */
const getLockedChoiceIndex = () => {
    const { game, player, pendingAnswer } = liveTriviaModule.state
    const questionIndex = game?.currentIndex
    if (questionIndex === null || questionIndex === undefined) {
        return null
    }
    if (pendingAnswer?.index === questionIndex) {
        return pendingAnswer.choiceIndex
    }
    return player?.answers[questionIndex] ?? null
}

const getCanAnswer = (serverNowMs: number) => {
    const game = liveTriviaModule.state.game
    if (game?.status !== 'question_open' || getPlayerStatus() !== 'playing') {
        return false
    }
    const closesAtMs = game.closesAt ? Date.parse(game.closesAt) : Number.NaN
    // The server keeps a 2s grace after closesAt for latency; the UI stops taking taps at closesAt.
    return Number.isNaN(closesAtMs) || serverNowMs < closesAtMs
}

export default {
    getIsLiveTriviaExam,
    getServerNowMs,
    getUpcomingGameForExam,
    getPlayerStatus,
    getLockedChoiceIndex,
    getCanAnswer,
}
